/*
 * /satellite-dish-alignment — страница. Геометрия — core.js; каталог позиций,
 * строки и подложка карты приходят от сервера (window.__DISH), который тем же
 * каталогом печатает таблицы по городам.
 *
 * Ответ один — «куда и на сколько повернуть тарелку» — и четыре способа
 * довести её туда (docs/satellite-dish-alignment.md):
 *   skew    — поворот конвертера картинкой, вид сзади тарелки;
 *   compass — компас телефона на штанге конвертера (честная точность, металл);
 *   sun     — момент, когда Солнце стоит точно в стороне спутника: без компаса;
 *   tilt    — наклон зеркала датчиком телефона на рейке по краю тарелки.
 */
import * as D from './core.js';

const SLUG = 'satellite-dish-alignment';
const W = window.__DISH || {};
const CAT = W.sats || {};
/* A transponder to tune per catalog position (resources/data/dish-aim/transponders.json, LyngSat): what to
   type into the receiver or the satfinder while aiming — the question that follows «where to point». */
const TP = W.tp || {};
// LyngSat writes the Russian packages in Latin letters; the page says them the way the viewer knows them.
const PKG_LOCAL = { Trikolor: 'Триколор', 'NTV Plus': 'НТВ-Плюс', MTS: 'МТС', Telekarta: 'Телекарта' };
function tpText(s) {
    const t = s && TP[s.id];
    if (!t) return '';
    const pkg = t.pkg && /^(ru|uk)/.test(LOC) ? PKG_LOCAL[t.pkg] || t.pkg : t.pkg;
    return tr('tp_line', {
        f: String(t.f), pol: tr('pol_' + t.pol), sr: String(t.sr), std: t.std, mod: t.mod || '', fec: t.fec || '',
        pkg: pkg ? tr('tp_pkg', { p: pkg }) : '', date: W.tpDate || '',
    }).replace(/\s{2,}/g, ' ').replace(/\s+([,.)])/g, '$1');
}
const I = W.i18n || {};
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const tr = (k, rep = {}) => String(I[k] != null ? I[k] : k).replace(/:([a-z_]+)/g, (m, n) => (rep[n] != null ? rep[n] : m));
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const LOC = (window.__intlLocale && window.__intlLocale()) || 'en';
const RTL = document.documentElement.getAttribute('dir') === 'rtl';
const ltr = (s) => (RTL ? '⁦' + s + '⁩' : s);
const nf = {};
function num(x, d = 1) {
    if (x == null || !isFinite(x)) return '—';
    if (!nf[d]) { try { nf[d] = new Intl.NumberFormat(LOC, { minimumFractionDigits: d, maximumFractionDigits: d }); } catch (e) { nf[d] = { format: (v) => v.toFixed(d) }; } }
    return nf[d].format(x);
}
const deg = (x, d = 1) => ltr(num(x, d) + '°');
function parse(v) {
    const s = String(v == null ? '' : v).trim().replace(/\s| /g, '').replace(',', '.');
    if (!s) return NaN;
    const x = Number(s);
    return x === x ? x : NaN;
}
function ae(action, extra) { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, action }, extra || {})); } catch (e) { /* optional */ } }
// Where the visit stands (tool_stage funnel; the last one rides on tool_abandon) and what refused.
function stage(name, meta) { try { window.__stage && window.__stage(name, meta || {}); } catch (e) { /* optional */ } }
function diag(issue, extra) { try { window.__aeDiagnostic && window.__aeDiagnostic(Object.assign({ issue }, extra || {})); } catch (e) { /* optional */ } }
function toast(msg, type) { try { window.__toast ? window.__toast(msg, type || 'info') : null; } catch (e) { /* optional */ } }
const POINTS = I.points || ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
const pointOf = (az) => POINTS[Math.round(D.norm360(az) / 22.5) % 16];
const lonText = (lon) => { const w = D.wrap180(lon); return tr(w >= 0 ? 'lon_e' : 'lon_w', { v: num(Math.abs(w), Math.abs(w) % 1 ? 1 : 0) }); };
const latLonText = (lat, lon) => ltr(tr(lat >= 0 ? 'lat_n' : 'lat_s', { v: num(Math.abs(lat), 3) }) + ' ' + tr(lon >= 0 ? 'lon_e' : 'lon_w', { v: num(Math.abs(lon), 3) }));

// ── state ────────────────────────────────────────────────────────────────
const S = {
    lat: W.startLat, lon: W.startLon, place: W.start, placeSrc: 'default',
    sat: W.defaultSat, custom: { value: NaN, side: 'e' }, tleName: '',
    kind: 'offset', offset: 24, d: 60, dishH: 0, focal: NaN, multi: '',
    way: 'skew',
};
const TLE = { state: 'idle', list: [], byName: {} };
let R = null;                // the last computed answer
let userTouched = false;     // tool_complete only for a person's own answer, not the example

function sat() {
    if (S.sat === 'custom') {
        const v = S.custom.value;
        if (!(v >= 0 && v <= 180)) return null;
        return { id: 'custom', name: lonText(S.custom.side === 'w' ? -v : v), lon: S.custom.side === 'w' ? -v : v, pol: 'unknown', band: 'ku', freq: 11.7, lo: [], tle: [] };
    }
    if (S.sat.startsWith('tle:')) {
        const t = TLE.byName[S.sat.slice(4)];
        if (!t) return null;
        return { id: S.sat, name: t.name, lon: t.lon, pol: 'unknown', band: 'ku', freq: 11.7, lo: [], tle: [t.name], live: t };
    }
    const c = CAT[S.sat];
    return c ? Object.assign({ id: S.sat }, c) : null;
}
function liveOf(s) {
    if (!s) return null;
    if (s.live) return s.live;
    for (const n of s.tle || []) if (TLE.byName[n]) return TLE.byName[n];
    return null;
}

// ── compute ──────────────────────────────────────────────────────────────
function compute() {
    const s = sat();
    if (!s || !isFinite(S.lat) || !isFinite(S.lon)) { R = null; return null; }
    const L = D.look(S.lat, S.lon, s.lon);
    const el = D.refracted(L.elevation);
    let decl = null;
    try { decl = window.Geomag ? window.Geomag.declination(S.lat, S.lon, new Date()) : null; } catch (e) { decl = null; }
    const mag = decl == null ? null : D.magnetic(L.azimuth, decl);
    const skew = D.skew(S.lat, S.lon, s.lon);
    const target = S.kind === 'offset' ? D.faceTilt(el, S.offset) : el;
    const bw = D.beamwidth(S.d / 100, s.freq || 11.7);
    R = { s, L, el, decl, mag, skew, target, bw, tol: D.errorForLoss(1, bw), live: liveOf(s) };
    return R;
}

function stat(label, value, sub, cls) {
    return `<div class="tc-stat${cls ? ' ' + cls : ''}"><div class="tc-stat-l">${esc(label)}</div><div class="tc-stat-v">${value}</div>${sub ? `<div class="tc-stat-s">${esc(sub)}</div>` : ''}</div>`;
}
function tiltWords(t) {
    if (Math.abs(t) < 0.05) return tr('tilt_vertical');
    return tr(t > 0 ? 'tilt_back' : 'tilt_fwd', { v: deg(Math.abs(t)) });
}
function skewWords(sk) {
    if (Math.abs(sk) < 0.5) return tr('skew_zero');
    return tr(sk > 0 ? 'skew_ccw' : 'skew_cw', { v: deg(Math.abs(sk)) });
}

function render() {
    const r = compute();
    const msgs = [];
    if (!r) {
        $('#da-answer-v').textContent = '—';
        $('#da-answer-sub').textContent = tr('need_sat');
        $('#da-stats').innerHTML = '';
        $('#da-msgs').innerHTML = '';
        $('#da-live-v').textContent = '—';
        drawSkew(null); renderMap(); return;
    }
    const { s, L, el } = r;
    const vis = L.elevation > -0.5;
    if (!vis) {
        $('#da-answer-v').textContent = tr('below_short');
        $('#da-answer-sub').textContent = tr('below_sub', { sat: s.name, lon: lonText(s.lon) });
        msgs.push(['bad', tr('below', { el: deg(L.elevation) })]);
        $('#da-stats').innerHTML = '';
    } else {
        const azMain = r.mag != null ? r.mag : L.azimuth;
        $('#da-answer-v').innerHTML = `${esc(deg(azMain))} <small>${esc(pointOf(L.azimuth))}</small> · ${esc(deg(el))}`;
        $('#da-answer-sub').textContent = r.mag != null
            ? tr('ans_sub', { az: deg(L.azimuth), decl: ltr((r.decl >= 0 ? '+' : '−') + num(Math.abs(r.decl), 1) + '°'), model: (window.Geomag && window.Geomag.MODEL) || 'WMM' })
            : tr('ans_sub_true', { az: deg(L.azimuth) });
        const live = r.live;
        const html = [
            stat(tr('st_el'), deg(el), tr('st_el_sub')),
            S.kind === 'offset' ? stat(tr('st_face'), deg(Math.abs(r.target)), tiltWords(r.target)) : '',
            s.pol === 'circular'
                ? stat(tr('st_skew'), esc(tr('skew_none')), tr('skew_none_sub'), 'good')
                : stat(tr('st_skew'), deg(Math.abs(r.skew)), Math.abs(r.skew) < 0.5 ? tr('skew_zero') : tr(r.skew > 0 ? 'skew_ccw_short' : 'skew_cw_short')),
            stat(tr('st_az_true'), deg(L.azimuth), pointOf(L.azimuth)),
            stat(tr('st_tol'), ltr('±' + num(r.tol, 1) + '°'), tr('st_tol_sub', { d: S.d })),
            stat(tr('st_range'), ltr(num(L.rangeKm, 0) + ' ' + tr('km')), tr('st_delay', { ms: num(L.delayMs, 0) })),
            live ? stat(tr('st_now'), esc(lonText(live.lon)), tr('st_now_sub', { i: num(live.inclination, 2) })) : '',
        ].join('');
        $('#da-stats').innerHTML = html;

        if (el < 5) msgs.push(['bad', tr('low_el5', { el: deg(el) })]);
        else if (el < 15) msgs.push(['warn', tr('low_el15', { el: deg(el) })]);
        if (live && Math.abs(live.driftPerDay) > 0.05) msgs.push(['bad', tr('drifting', { name: live.name, v: num(Math.abs(live.driftPerDay), 2) })]);
        else if (live && live.inclination > 1) msgs.push(['warn', tr('inclined', { name: live.name, i: num(live.inclination, 1) })]);
        if (s.band === 'c') msgs.push(['warn', tr('band_c')]);
        if (s.band === 's') msgs.push(['warn', tr('band_s')]);
        if (s.pol === 'circular') msgs.push(['good', tr('lnb_circ')]);
        else if (s.pol === 'linear') msgs.push(['', tr('lnb_lin')]);
        else if (s.pol === 'mixed') msgs.push(['', tr('lnb_mixed')]);
        const tpLine = tpText(s);
        if (tpLine) msgs.push(['', tpLine]);
        const near = neighbours(s, 3.2);
        if (near.length) msgs.push(['warn', tr('neighbours', { list: near.map((n) => `${n.name} (${lonText(n.lon)})`).join(', ') })]);
        if (r.decl == null) msgs.push(['warn', tr('no_decl')]);
        if (el > 60 && S.kind === 'offset') msgs.push(['', tr('high_el')]);
    }
    if (S.placeSrc === 'default') msgs.unshift(['warn', tr('example', { place: S.place })]);
    $('#da-msgs').innerHTML = msgs.map(([cls, text]) => `<p class="tc-msg ${cls}">${esc(text)}</p>`).join('');
    $('#da-live-v').textContent = vis ? `${r.mag != null ? deg(r.mag) : deg(L.azimuth)} · ${deg(el)}` : tr('below_short');

    drawSkew(r);
    renderTiltTarget();
    renderObstTable();
    renderMap();
    if (S.way === 'sun') renderSun();
    if ($('#da-d-multi').open) renderMulti();
    if ($('#da-d-outage').open) renderOutage();
    if (compassOn) updateDial();
    save();
    complete();
}

function neighbours(s, within) {
    const out = [];
    for (const [id, c] of Object.entries(CAT)) {
        if (id === s.id) continue;
        const dl = Math.abs(D.angleDiff(c.lon, s.lon));
        if (dl > 0.4 && dl <= within) out.push({ name: c.name, lon: c.lon });
    }
    return out.sort((a, b) => Math.abs(D.angleDiff(a.lon, s.lon)) - Math.abs(D.angleDiff(b.lon, s.lon))).slice(0, 4);
}

let completedKey = '';
function complete() {
    if (!userTouched || !R || R.L.elevation <= 0) return;
    const key = `${S.sat}|${S.lat.toFixed(2)}|${S.lon.toFixed(2)}`;
    if (key === completedKey) return;
    completedKey = key;
    try { window.__aeToolComplete && window.__aeToolComplete({ sat: S.sat.startsWith('tle:') ? 'tle' : S.sat, src: S.placeSrc, el: Math.round(R.el / 5) * 5, kind: S.kind }); } catch (e) { /* optional */ }
}

// ── skew picture: the LNB seen from behind the dish, looking at the satellite ──
function drawSkew(r) {
    const svg = $('#da-skew-svg');
    const val = $('#da-skew-v'), cap = $('#da-skew-cap');
    if (!r || r.L.elevation <= 0) { svg.innerHTML = ''; val.textContent = '—'; cap.textContent = ''; return; }
    if (r.s.pol === 'circular') {
        svg.innerHTML = lnbSvg(0, false);
        val.textContent = tr('skew_none');
        cap.textContent = tr('skew_circ_cap');
        return;
    }
    const sk = r.skew;
    svg.innerHTML = lnbSvg(sk, true);
    val.textContent = Math.abs(sk) < 0.5 ? tr('skew_zero') : `${deg(Math.abs(sk))} ${tr(sk > 0 ? 'skew_ccw_short' : 'skew_cw_short')}`;
    cap.textContent = (r.s.pol === 'mixed' ? tr('skew_mixed_cap') + ' ' : '') + tr('skew_cap');
}
function lnbSvg(sk, arrow) {
    // Screen angles: 0 = up, clockwise positive. A positive skew is anticlockwise.
    const a = -sk, cx = 110, cy = 110, R0 = 58;
    const pt = (r, ang) => { const t = (ang - 90) * Math.PI / 180; return [(cx + r * Math.cos(t)).toFixed(1), (cy + r * Math.sin(t)).toFixed(1)]; };
    let extra = '';
    if (arrow && Math.abs(sk) >= 0.5) {
        // Which way: a fixed 70° arrow over the top, so a 3° turn still reads at a glance.
        const cw = a > 0, from = cw ? -35 : 35, to = cw ? 35 : -35, r1 = 92;
        const p0 = pt(r1, from), p1 = pt(r1, to);
        const h1 = pt(r1 - 9, to + (cw ? -9 : 9)), h2 = pt(r1 + 9, to + (cw ? -9 : 9));
        extra += `<path d="M${p0[0]} ${p0[1]} A${r1} ${r1} 0 0 ${cw ? 1 : 0} ${p1[0]} ${p1[1]}" fill="none" stroke="var(--lg-accent)" stroke-width="5" stroke-linecap="round"/>` +
                 `<path d="M${p1[0]} ${p1[1]} L${h1[0]} ${h1[1]} L${h2[0]} ${h2[1]}Z" fill="var(--lg-accent)" stroke="var(--lg-accent)" stroke-width="3" stroke-linejoin="round"/>`;
        // How much: the true angle as a wedge under the LNB, from «connector down».
        const w0 = pt(104, 180), w1 = pt(104, 180 + a);
        extra += `<path d="M${cx} ${cy} L${w0[0]} ${w0[1]} A104 104 0 0 ${cw ? 1 : 0} ${w1[0]} ${w1[1]}Z" fill="var(--lg-accent)" fill-opacity=".18"/>` +
                 `<line x1="${cx}" y1="${cy}" x2="${w1[0]}" y2="${w1[1]}" stroke="var(--lg-accent)" stroke-width="2.5"/>`;
    }
    const z = pt(106, 180);
    return `<circle cx="${cx}" cy="${cy}" r="104" fill="none" stroke="var(--lg-border)" stroke-dasharray="3 5"/>` +
        `<line x1="${cx}" y1="${cy}" x2="${z[0]}" y2="${z[1]}" stroke="var(--lg-text-3)" stroke-width="2" stroke-dasharray="4 4"/>` +
        extra +
        `<g transform="rotate(${a.toFixed(2)} ${cx} ${cy})">` +
        `<circle cx="${cx}" cy="${cy}" r="${R0}" fill="var(--lg-surface-2)" stroke="var(--lg-text-2)" stroke-width="3"/>` +
        `<circle cx="${cx}" cy="${cy}" r="${R0 - 20}" fill="none" stroke="var(--lg-border)" stroke-width="2"/>` +
        `<rect x="${cx - 9}" y="${cy + R0 - 4}" width="18" height="24" rx="3" fill="var(--lg-text-2)"/>` +
        `<rect x="${cx - 5}" y="${cy + R0 + 18}" width="10" height="8" rx="2" fill="var(--lg-text-3)"/>` +
        `</g>` +
        `<text x="${cx}" y="${cy + 5}" text-anchor="middle" font-size="13" font-weight="700" fill="var(--lg-text-2)">LNB</text>`;
}

// ── map ──────────────────────────────────────────────────────────────────
let map = null, siteMarker = null, line = null, satLabel = null, obstLayer = null, obstMode = false;
function initMap() {
    if (map || !window.L) return;
    const Lf = window.L;
    map = Lf.map('da-map', { zoomControl: true, worldCopyJump: true, attributionControl: true }).setView([S.lat, S.lon], 17);
    const tile = W.tile || {};
    if (window.__mapTiles) {
        window.__mapTiles.base(Lf, map, null, { slug: SLUG, style: tile.style, styles: tile.styles, labels: tile.labels, text: tile.offline, opts: { maxZoom: 19 } });
    } else if (window.__basemapFallback) {
        window.__basemapFallback(Lf, map, '');
    }
    siteMarker = Lf.marker([S.lat, S.lon], { draggable: true, keyboard: true, title: tr('map_site'), alt: tr('map_site') }).addTo(map);
    siteMarker.on('dragend', () => { const p = siteMarker.getLatLng(); setPlace(p.lat, p.lng, null, 'map'); });
    obstLayer = Lf.layerGroup().addTo(map);
    map.on('click', (e) => {
        if (obstMode) { placeObstacle(e.latlng); return; }
        setPlace(e.latlng.lat, e.latlng.lng, null, 'map');
    });
    map.on('zoomend moveend', drawLine);
    renderMap();
}
function drawLine() {
    if (!map || !R) return;
    const Lf = window.L;
    if (line) { line.remove(); line = null; }
    if (satLabel) { satLabel.remove(); satLabel = null; }
    if (R.L.elevation <= -0.5) return;
    const b = map.getBounds(), span = map.distance(b.getSouthWest(), b.getNorthEast());
    const len = Math.max(200, span * 1.2), pts = [];
    for (let i = 0; i <= 16; i++) { const p = D.destination(S.lat, S.lon, R.L.azimuth, len * i / 16); pts.push([p.lat, p.lon]); }
    line = Lf.polyline(pts, { color: '#0891b2', weight: 4, opacity: 0.95, interactive: false }).addTo(map);
    // The label sits ~110 px from the dish along the line, whatever the zoom.
    const size = map.getSize(), px = Math.min(110, Math.min(size.x, size.y) * 0.35);
    const mPerPx = map.distance(map.containerPointToLatLng([0, size.y / 2]), map.containerPointToLatLng([px, size.y / 2]));
    const lab = D.destination(S.lat, S.lon, R.L.azimuth, mPerPx);
    satLabel = Lf.marker([lab.lat, lab.lon], {
        interactive: false, keyboard: false,
        icon: Lf.divIcon({ className: '', html: `<span class="da-sat-label">→ ${esc(R.s.name)} · ${esc(deg(R.L.azimuth))}</span>`, iconSize: null }),
    }).addTo(map);
}
function renderMap() {
    if (!map) return;
    siteMarker.setLatLng([S.lat, S.lon]);
    // Pan only when the dish left the view: a tap that moved it stays where the finger was.
    if (!map.getBounds().pad(-0.1).contains([S.lat, S.lon])) map.setView([S.lat, S.lon], Math.max(map.getZoom(), 15), { animate: false });
    drawLine();
}
function placeObstacle(ll) {
    const out = $('#da-obst-out');
    if (!R || R.L.elevation <= 0) return;
    obstLayer.clearLayers();
    const o = D.obstacleOnLine(S.lat, S.lon, R.L.azimuth, ll.lat, ll.lng);
    window.L.circleMarker(ll, { radius: 7, color: '#ca8a04', weight: 3, fillColor: '#fde047', fillOpacity: 0.9 }).addTo(obstLayer);
    ae('obstacle_tap');
    if (!o) { out.innerHTML = `<p class="tc-msg good">${esc(tr('obst_behind'))}</p>`; return; }
    const h = D.clearance(o.along, R.el, S.dishH);
    const side = Math.abs(o.across);
    const lines = [tr('obst_at', { along: num(o.along, 0), across: num(side, 0) }), tr('obst_height', { h: num(h, 1) })];
    if (side > 5) lines.push(tr('obst_aside'));
    out.innerHTML = `<p class="tc-msg ${side > 5 ? '' : 'warn'}">${lines.map(esc).join(' ')}</p>`;
    window.L.marker(ll, { interactive: false, icon: window.L.divIcon({ className: '', html: `<span class="da-obst-label">≤ ${esc(num(h, 1))} ${esc(tr('m'))}</span>`, iconSize: null, iconAnchor: [-8, 10] }) }).addTo(obstLayer);
}

// ── place ────────────────────────────────────────────────────────────────
let locWidget = null;
function setPlace(lat, lon, name, src) {
    if (!isFinite(lat) || !isFinite(lon)) return;
    S.lat = lat; S.lon = D.wrap180(lon); S.place = name || ''; S.placeSrc = src || 'map';
    userTouched = true;
    if (locWidget && src !== 'city') { try { locWidget.setExternal(lat, lon, null, name || null); } catch (e) { /* optional */ } }
    if (obstLayer) obstLayer.clearLayers();
    $('#da-obst-out').innerHTML = '';
    showPlace();
    ae('place', { src: S.placeSrc });
    render();
}
function showPlace() {
    const where = latLonText(S.lat, S.lon);
    $('#da-place').textContent = S.place ? `${S.place} · ${where}` : where;
}

// ── satellite choice ─────────────────────────────────────────────────────
function setSat(id, how) {
    S.sat = id;
    // The boot call shows the example; only a person's own choice makes the answer theirs
    // (it used to count every page view as a finished result).
    if (how !== 'boot') userTouched = true;
    $$('.da-preset').forEach((b) => { const on = b.dataset.sat === id; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    const sel = $('#da-sat');
    if (id === 'custom' || CAT[id]) sel.value = id; else sel.value = 'custom';
    $('#da-custom').hidden = id !== 'custom';
    if (!id.startsWith('tle:')) $('#da-find').value = '';
    if (how !== 'boot') ae('sat', { how, sat: id.startsWith('tle:') ? 'tle' : id });
    render();
}

function loadTle() {
    if (TLE.state === 'loading' || TLE.state === 'ok') return Promise.resolve();
    TLE.state = 'loading';
    $('#da-find-hint').textContent = tr('find_loading');
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), 20000) : null;
    return fetch(W.tleUrl, { credentials: 'omit', signal: ctl ? ctl.signal : undefined })
        .then((r) => (r.ok ? r.text() : ''))
        .then((txt) => {
            const lines = String(txt || '').split(/\r?\n/);
            const list = [];
            for (let i = 0; i + 2 < lines.length; i++) {
                if (lines[i + 1].startsWith('1 ') && lines[i + 2].startsWith('2 ')) {
                    const g = D.geoFromTle(lines[i + 1], lines[i + 2]);
                    const name = lines[i].trim();
                    if (g && name) { const t = Object.assign({ name }, g); list.push(t); TLE.byName[name] = t; }
                    i += 2;
                }
            }
            if (list.length < 20) throw new Error('tle_empty');
            TLE.list = list.sort((a, b) => b.lon - a.lon);
            TLE.state = 'ok';
            $('#da-find-list').innerHTML = TLE.list.map((t) => `<option value="${esc(t.name)}">${esc(lonText(t.lon))}</option>`).join('');
            $('#da-find-hint').textContent = tr('find_ready', { n: TLE.list.length });
            render();
        })
        .catch((e) => {
            TLE.state = 'fail';
            $('#da-find-hint').textContent = tr('find_fail');
            try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'tle_unavailable', detail: String((e && e.message) || e).slice(0, 80) }); } catch (_) { /* optional */ }
        })
        .finally(() => { if (timer) clearTimeout(timer); });
}

// ── ways to aim ──────────────────────────────────────────────────────────
function setWay(w) {
    S.way = w;
    $$('.da-ways .tc-seg-b').forEach((b) => { const on = b.dataset.way === w; b.classList.toggle('on', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); });
    $$('.da-way').forEach((p) => { p.hidden = p.dataset.pane !== w; });
    if (w !== 'compass') stopCompass();
    if (w !== 'tilt') stopTilt();
    if (w !== 'ar') stopAr();
    if (w === 'sun') renderSun();
    ae('way', { way: w });
    save();
}

// compass on the LNB arm
let cc = null, compassOn = false, lastHeading = null;
function startCompass() {
    if (!window.CompassCore) { toast(tr('sensor_missing'), 'error'); return; }
    if (!cc) {
        cc = new window.CompassCore({ smooth: 0.2 });
        cc.onUpdate((st) => { lastHeading = st; updateDial(); });
    }
    if (R && R.decl != null) cc.setDeclination(R.decl);
    cc.start().then((res) => {
        if (res.permission === 'granted') {
            compassOn = true;
            $('#da-dial').hidden = false;
            $('#da-compass-go').textContent = tr('compass_stop');
            $('#da-dial-say').textContent = tr('compass_wait');
            ae('compass_on');
            stage('compass');
            setTimeout(() => { if (compassOn && !lastHeading) { $('#da-dial-say').textContent = tr('compass_silent'); diag('compass_silent'); } }, 3500);
        } else if (res.permission === 'unsupported') {
            toast(tr('compass_unsupported'), 'error');
            diag('compass_unsupported');
        } else {
            toast(tr('compass_denied'), 'error');
            diag('compass_denied');
        }
    });
}
function stopCompass() {
    if (cc && !AR.on) cc.stop();
    compassOn = false; lastHeading = null;
    const b = $('#da-compass-go'); if (b) b.textContent = tr('compass_go_label');
    const d = $('#da-dial'); if (d) d.hidden = true;
}
function updateDial() {
    if (!compassOn || !R) return;
    if (R.decl != null && cc && cc.declination !== R.decl) cc.setDeclination(R.decl);
    const st = lastHeading, say = $('#da-dial-say'), acc = $('#da-dial-acc');
    if (!st) return;
    if (!st.absolute) { say.textContent = tr('compass_relative'); drawDial(null); acc.textContent = ''; return; }
    const h = st.headingTrue != null ? st.headingTrue : st.headingMagnetic;
    const diff = D.angleDiff(R.L.azimuth, h);
    drawDial(diff);
    const tol = Math.max(1, R.tol);
    say.textContent = Math.abs(diff) <= tol ? tr('compass_on_target') : tr(diff > 0 ? 'turn_right' : 'turn_left', { v: deg(Math.abs(diff), 0) });
    const parts = [tr('compass_now', { h: deg(h, 0), t: deg(R.L.azimuth, 0) })];
    if (st.accuracyDeg != null) parts.push(tr('compass_acc', { v: num(st.accuracyDeg, 0) }));
    if (st.interference) parts.push(tr('compass_iron'));
    acc.textContent = parts.join(' · ');
}
function drawDial(diff) {
    const svg = $('#da-dial-svg'), c = 120;
    let ticks = '';
    for (let a = 0; a < 360; a += 15) {
        const r1 = a % 90 === 0 ? 92 : 100, rr = (a - 90) * Math.PI / 180;
        ticks += `<line x1="${(c + r1 * Math.cos(rr)).toFixed(1)}" y1="${(c + r1 * Math.sin(rr)).toFixed(1)}" x2="${(c + 108 * Math.cos(rr)).toFixed(1)}" y2="${(c + 108 * Math.sin(rr)).toFixed(1)}" stroke="var(--lg-text-3)" stroke-width="2"/>`;
    }
    let target = '';
    if (diff != null) {
        const rr = (diff - 90) * Math.PI / 180, ok = Math.abs(diff) <= Math.max(1, R ? R.tol : 1);
        const col = ok ? 'var(--lg-success)' : 'var(--lg-accent)';
        target = `<line x1="${c}" y1="${c}" x2="${(c + 84 * Math.cos(rr)).toFixed(1)}" y2="${(c + 84 * Math.sin(rr)).toFixed(1)}" stroke="${col}" stroke-width="6" stroke-linecap="round"/>` +
                 `<circle cx="${(c + 92 * Math.cos(rr)).toFixed(1)}" cy="${(c + 92 * Math.sin(rr)).toFixed(1)}" r="11" fill="${col}"/>` +
                 `<circle cx="${(c + 92 * Math.cos(rr)).toFixed(1)}" cy="${(c + 92 * Math.sin(rr)).toFixed(1)}" r="4" fill="#fff"/>`;
    }
    svg.innerHTML = `<circle cx="${c}" cy="${c}" r="110" fill="var(--lg-surface-2)" stroke="var(--lg-border)" stroke-width="2"/>${ticks}` +
        `<path d="M${c} 6 L${c - 12} 30 L${c + 12} 30Z" fill="var(--lg-text)"/>` +
        `<rect x="${c - 22}" y="${c - 40}" width="44" height="80" rx="9" fill="none" stroke="var(--lg-text-2)" stroke-width="3"/>` + target;
}

// tilt: the phone on a straight rail across the rim, screen facing out of the dish
let tiltOn = false, tiltSm = null, tiltRoll = null;
function onTilt(e) {
    if (e.beta == null || !window.CompassCore) return;
    const C = window.CompassCore, Rm = C.rotationMatrix(e.alpha || 0, e.beta, e.gamma || 0);
    // The back of the phone lies on the rim, so the face normal is the reverse of the camera axis.
    const face = -C.elevationOf(C.toEarth(Rm, [0, 0, -1]));
    const roll = C.elevationOf(C.toEarth(Rm, [1, 0, 0]));
    tiltSm = tiltSm == null ? face : tiltSm + (face - tiltSm) * 0.25;
    tiltRoll = tiltRoll == null ? roll : tiltRoll + (roll - tiltRoll) * 0.25;
    drawLevel();
}
function startTilt() {
    const DOE = window.DeviceOrientationEvent;
    if (!DOE) { toast(tr('compass_unsupported'), 'error'); return; }
    const go = () => {
        window.addEventListener('deviceorientation', onTilt, { passive: true });
        tiltOn = true; tiltSm = null; tiltRoll = null;
        $('#da-level').hidden = false;
        $('#da-tilt-go').textContent = tr('tilt_stop');
        $('#da-level-say').textContent = tr('compass_wait');
        ae('tilt_on');
        stage('tilt');
        setTimeout(() => { if (tiltOn && tiltSm == null) { $('#da-level-say').textContent = tr('tilt_silent'); diag('tilt_silent'); } }, 3500);
    };
    const denied = () => { toast(tr('compass_denied'), 'error'); diag('tilt_denied'); };
    if (typeof DOE.requestPermission === 'function') {
        DOE.requestPermission().then((r) => (r === 'granted' ? go() : denied())).catch(denied);
    } else go();
}
function stopTilt() {
    window.removeEventListener('deviceorientation', onTilt);
    tiltOn = false;
    const b = $('#da-tilt-go'); if (b) b.textContent = tr('tilt_go_label');
    const l = $('#da-level'); if (l) l.hidden = true;
}
function drawLevel() {
    if (!tiltOn || !R || tiltSm == null) return;
    const target = R.target, cur = tiltSm, diff = target - cur;
    const svg = $('#da-level-svg'), cx = 120, cy = 128;
    // Side view: the dish face as a line through (cx, cy); 0° = vertical, positive leans back.
    const face = (t, col, w, dash) => {
        const r = (t) * Math.PI / 180, L = 100;
        return `<line x1="${(cx - Math.sin(r) * 0).toFixed(1)}" y1="${cy}" x2="${(cx + Math.sin(r) * L).toFixed(1)}" y2="${(cy - Math.cos(r) * L).toFixed(1)}" stroke="${col}" stroke-width="${w}" stroke-linecap="round"${dash ? ' stroke-dasharray="6 6"' : ''}/>`;
    };
    const ok = Math.abs(diff) <= 0.5;
    svg.innerHTML = `<line x1="10" y1="${cy}" x2="230" y2="${cy}" stroke="var(--lg-border)" stroke-width="2"/>` +
        face(target, 'var(--lg-text-3)', 3, true) + face(cur, ok ? 'var(--lg-success)' : 'var(--lg-accent)', 7, false) +
        `<text x="18" y="22" font-size="13" fill="var(--lg-text-3)">${esc(tr('level_front'))}</text>` +
        `<text x="222" y="22" font-size="13" text-anchor="end" fill="var(--lg-text-3)">${esc(tr('level_back'))}</text>`;
    const parts = [ok ? tr('tilt_on_target') : tr(diff > 0 ? 'tilt_more_back' : 'tilt_more_fwd', { v: deg(Math.abs(diff)) })];
    $('#da-level-say').textContent = parts.join(' ');
    const extra = [tr('tilt_now', { now: tiltWords(cur), need: tiltWords(target) })];
    if (tiltRoll != null && Math.abs(tiltRoll) > 2) extra.push(tr('tilt_roll', { v: deg(Math.abs(tiltRoll)) }));
    let note = $('#da-level-note');
    if (!note) { note = document.createElement('p'); note.id = 'da-level-note'; note.className = 'tc-hint'; $('#da-level').appendChild(note); }
    note.textContent = extra.join(' ');
}
function renderTiltTarget() {
    if (!R) { $('#da-tilt-target').innerHTML = ''; return; }
    $('#da-tilt-hint').textContent = tr(S.kind === 'offset' ? 'tilt_hint_offset_js' : 'tilt_hint_prime', { off: deg(S.offset, 0) });
    $('#da-tilt-target').innerHTML =
        stat(tr('st_face'), deg(Math.abs(R.target)), tiltWords(R.target), 'lead') +
        stat(tr('st_el'), deg(R.el), S.kind === 'offset' ? tr('tilt_offset_sub', { off: deg(S.offset, 0) }) : tr('st_el_sub'));
    if (tiltOn) drawLevel();
}

// sun: when is the Sun exactly in the satellite's direction
function zoneDayStart(tz, daysAhead) {
    const PT = window.__placeTime;
    const now = new Date(Date.now() + daysAhead * 86400000);
    let y, m, d;
    try {
        const p = {};
        new Intl.DateTimeFormat('en-US', { timeZone: tz || undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now).forEach((x) => { p[x.type] = x.value; });
        y = +p.year; m = +p.month; d = +p.day;
    } catch (e) { y = now.getUTCFullYear(); m = now.getUTCMonth() + 1; d = now.getUTCDate(); }
    const guess = Date.UTC(y, m - 1, d);
    const off = PT ? PT.offsetMinutes(tz, new Date(guess)) : -new Date(guess).getTimezoneOffset();
    return guess - off * 60000;
}
function renderSun() {
    const out = $('#da-sun-out');
    if (!R || R.L.elevation <= 0 || !window.AstroCore) { out.innerHTML = ''; return; }
    const PT = window.__placeTime, tz = PT ? PT.zoneOf(S.lat, S.lon) : null;
    const fmtT = (t) => (PT ? PT.format(new Date(t), tz, LOC, { hour: '2-digit', minute: '2-digit' }) : new Date(t).toLocaleTimeString(LOC, { hour: '2-digit', minute: '2-digit' }));
    const fmtD = (t) => (PT ? PT.format(new Date(t), tz, LOC, { weekday: 'short', day: 'numeric', month: 'long' }) : new Date(t).toLocaleDateString(LOC));
    const zoneLabel = PT ? PT.label(tz, new Date(), LOC) : '';
    const rows = [];
    for (let k = 0; k < 3; k++) {
        const day = zoneDayStart(tz, k);
        const sun = D.sunAlignTimes(window.AstroCore.sunPos, S.lat, S.lon, R.L.azimuth, day);
        const shadow = D.sunAlignTimes(window.AstroCore.sunPos, S.lat, S.lon, D.norm360(R.L.azimuth + 180), day);
        for (const h of sun) rows.push({ t: h.t, kind: 'sun', el: h.sunElevation });
        for (const h of shadow) rows.push({ t: h.t, kind: 'shadow', el: h.sunElevation });
    }
    rows.sort((a, b) => a.t - b.t);
    const upcoming = rows.filter((r) => r.t > Date.now() - 10 * 60000).slice(0, 4);
    if (!upcoming.length) { out.innerHTML = `<p class="tc-msg warn">${esc(tr('sun_none'))}</p>`; return; }
    // The Sun on the satellite's azimuth AND at its height is inside the beam: that is the outage itself.
    const inBeam = upcoming.some((r) => r.kind === 'sun' && Math.abs(r.el - R.el) < R.bw / 2 + 0.6);
    const glare = upcoming.some((r) => r.kind === 'sun' && Math.abs(r.el - R.el) < 6);
    out.innerHTML = upcoming.map((r) => `<div class="da-sun-row"><div><div class="tc-stat-l">${esc(fmtD(r.t))}</div><b>${esc(fmtT(r.t))}</b> <span class="tc-hint">${esc(zoneLabel)}</span></div>` +
        `<div class="tc-hint da-sun-what">${esc(tr(r.kind === 'sun' ? 'sun_row_sun' : 'sun_row_shadow', { el: deg(r.el, 0) }))}</div></div>`).join('') +
        (inBeam ? `<p class="tc-msg warn">${esc(tr('sun_in_beam'))}</p>` : glare ? `<p class="tc-msg warn">${esc(tr('sun_glare'))}</p>` : '');
}

// camera: the belt and the satellite over the live picture (AR)
const FOV_LONG = 68;          // the long side of a phone's main camera, degrees (26 mm equivalent)
const AR = { on: false, cam: null, raf: 0, cal: 0, calibrated: false, since: 0, northSaid: false };
function screenAngle() {
    try {
        if (screen.orientation && typeof screen.orientation.angle === 'number') return screen.orientation.angle;
        if (typeof window.orientation === 'number') return window.orientation;
    } catch (e) { /* none */ }
    return 0;
}
async function startAr() {
    if (!window.CameraManager || !window.CompassCore) { toast(tr('sensor_missing'), 'error'); return; }
    const btn = $('#da-ar-go');
    if (!cc) { cc = new window.CompassCore({ smooth: 0.15 }); cc.onUpdate((st) => { lastHeading = st; if (compassOn) updateDial(); }); }
    if (R && R.decl != null) cc.setDeclination(R.decl);
    // The sensor permission must be asked first, inside the tap (iOS).
    const perm = await cc.start();
    if (perm.permission !== 'granted') { toast(tr(perm.permission === 'unsupported' ? 'compass_unsupported' : 'compass_denied'), 'error'); diag('ar_sensor_' + perm.permission); return; }
    btn.disabled = true;
    try {
        AR.cam = AR.cam || new window.CameraManager({ slug: SLUG });
        const stream = await AR.cam.start({ facingMode: 'environment', width: 1280, height: 720 });
        $('#da-ar').hidden = false;
        if (!(await AR.cam.show($('#da-ar-video'), stream, { anchor: btn }))) { stopAr(); return; }
        AR.on = true;
        btn.textContent = tr('ar_stop');
        $('#da-ar-sun').hidden = false;
        ae('ar_on');
        stage('ar');
        AR.since = Date.now(); AR.northSaid = false;
        const loop = () => { if (!AR.on) return; AR.raf = requestAnimationFrame(loop); drawAr(); };
        loop();
    } catch (e) {
        stopAr();   // the camera door explains the refusal at the button
    } finally { btn.disabled = false; }
}
function stopAr() {
    AR.on = false;
    if (AR.raf) cancelAnimationFrame(AR.raf);
    AR.raf = 0;
    try { if (AR.cam) AR.cam.stop(); } catch (e) { /* already stopped */ }
    if (cc && !compassOn) cc.stop();
    const b = $('#da-ar-go'); if (b) b.textContent = tr('ar_go_label');
    const box = $('#da-ar'); if (box) box.hidden = true;
    const sb = $('#da-ar-sun'); if (sb) sb.hidden = true;
}
function calibrateBySun() {
    const Rm = cc && cc.earthMatrix(true);
    if (!Rm || !window.AstroCore) { toast(tr('compass_relative'), 'error'); return; }
    const aim = D.cameraAim(Rm);
    const sun = D.sunAzEl(window.AstroCore.sunPos, new Date(), S.lat, S.lon);
    const sunEl = D.refracted(sun.elevation);
    if (sunEl < 1) { toast(tr('ar_sun_down'), 'error'); return; }
    if (Math.abs(sunEl - aim.elevation) > 8) { toast(tr('ar_sun_far'), 'error'); return; }
    AR.cal = D.angleDiff(sun.azimuth, aim.azimuth);
    AR.calibrated = true;
    ae('ar_sun_cal', { off: Math.round(AR.cal) });
    toast(tr('ar_cal_done', { v: ltr((AR.cal >= 0 ? '+' : '−') + num(Math.abs(AR.cal), 1) + '°') }), 'success');
}
function drawAr() {
    const box = $('#da-ar'), cv = $('#da-ar-canvas'), video = $('#da-ar-video');
    const w = box.clientWidth, h = box.clientHeight, dpr = Math.min(2, window.devicePixelRatio || 1);
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
    const g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    const say = $('#da-ar-say'), info = $('#da-ar-info');
    if (!R || R.L.elevation <= 0) { say.textContent = tr('below_short'); return; }
    if (cc && R.decl != null && cc.declination !== R.decl) cc.setDeclination(R.decl);
    let Rm = cc && cc.earthMatrix(true);
    if (!Rm) {
        // iPhone: webkitCompassHeading is read in the level plane, so CompassCore ties the
        // camera to north only after the phone lay (near) flat once. Held up at the sky from
        // the start it waits forever — say what to do instead of «waiting for the compass».
        const ios = cc && cc.state && cc.state.source === 'ios';
        say.textContent = ios ? tr('ar_ios_flat') : (lastHeading && !lastHeading.absolute ? tr('compass_relative') : tr('compass_wait'));
        if (!AR.northSaid && AR.since && Date.now() - AR.since > 8000) { AR.northSaid = true; diag('ar_no_north', { src: (cc && cc.state && cc.state.source) || 'none' }); }
        return;
    }
    Rm = D.yawMatrix(Rm, AR.cal);
    const vw = video.videoWidth || 1280, vh = video.videoHeight || 720;
    const sc = Math.max(w / vw, h / vh);
    const f = (Math.max(vw, vh) * sc / 2) / Math.tan(FOV_LONG / 2 * Math.PI / 180);
    const ang = screenAngle();
    const P = (az, el) => D.project(Rm, D.enuOf(az, el), f, w, h, ang);
    const line = (pts, color, width, dash) => {
        g.strokeStyle = color; g.lineWidth = width; g.setLineDash(dash || []);
        g.beginPath();
        let pen = false;
        for (const p of pts) {
            const ok = p.front && Math.abs(p.x) < 4 * w && Math.abs(p.y) < 4 * h;
            if (ok && pen) g.lineTo(p.x, p.y); else if (ok) { g.moveTo(p.x, p.y); pen = true; } else pen = false;
        }
        g.stroke(); g.setLineDash([]);
    };
    // Labels never overlap: the target and the sun claim their place first, a
    // neighbour whose label would land on another keeps only its dot.
    const placed = [];
    const label = (x, y, text, color) => {
        g.font = '600 13px system-ui, sans-serif';
        // A right-to-left page makes canvas text right-anchored by default: the box below assumes left.
        g.direction = 'ltr'; g.textAlign = 'left';
        const tw = g.measureText(text).width;
        let lx = x + 8;
        if (lx + tw + 10 > w) lx = x - 8 - tw - 10;
        const r = [lx, y - 10, tw + 10, 20];
        if (r[0] < 0 || r[1] < 0 || r[1] + r[3] > h) return;
        if (placed.some((q) => r[0] < q[0] + q[2] && q[0] < r[0] + r[2] && r[1] < q[1] + q[3] && q[1] < r[1] + r[3])) return;
        placed.push(r);
        g.fillStyle = 'rgba(0,0,0,.6)'; g.fillRect(r[0], r[1], r[2], r[3]);
        g.fillStyle = color; g.fillText(text, r[0] + 5, y + 5);
    };
    // Horizon.
    const hz = []; for (let a = 0; a <= 360; a += 3) hz.push(P(a, 0));
    line(hz, 'rgba(255,255,255,.55)', 1.5, [6, 6]);
    // The belt, then every catalogue position on it, then the target.
    line(D.clarkeBelt(S.lat, S.lon, 1).map((b) => P(b.az, b.el)), 'rgba(56,189,248,.9)', 2.5);
    const t = P(R.L.azimuth, R.el);
    const rBeam = Math.max(14, f * Math.tan((R.bw / 2) * Math.PI / 180));
    if (t.front) {
        g.strokeStyle = '#facc15'; g.lineWidth = 3; g.beginPath(); g.arc(t.x, t.y, rBeam, 0, 7); g.stroke();
        g.fillStyle = '#facc15'; g.beginPath(); g.arc(t.x, t.y, 4, 0, 7); g.fill();
        label(t.x + rBeam - 6, t.y, `${R.s.name} · ${lonText(R.s.lon)}`, '#fde68a');
    }
    // The sun, for calibration.
    if (window.AstroCore) {
        const sun = D.sunAzEl(window.AstroCore.sunPos, new Date(), S.lat, S.lon);
        if (sun.elevation > -1) {
            const p = P(sun.azimuth, D.refracted(sun.elevation));
            if (p.front) { g.strokeStyle = '#fb923c'; g.lineWidth = 2; g.beginPath(); g.arc(p.x, p.y, 16, 0, 7); g.stroke(); label(p.x + 10, p.y, tr('ar_sun'), '#fed7aa'); }
        }
    }
    // Neighbours, nearest to the target first (they win the label space).
    const others = Object.entries(CAT).filter(([id]) => id !== R.s.id).sort((a, b) => Math.abs(D.angleDiff(a[1].lon, R.s.lon)) - Math.abs(D.angleDiff(b[1].lon, R.s.lon)));
    for (const [id, c] of others) {
        if (id === R.s.id) continue;
        const L = D.look(S.lat, S.lon, c.lon);
        if (L.elevation <= 0) continue;
        const p = P(L.azimuth, D.refracted(L.elevation));
        if (!p.front || p.x < -40 || p.x > w + 40 || p.y < -20 || p.y > h + 20) continue;
        g.fillStyle = 'rgba(56,189,248,.95)'; g.beginPath(); g.arc(p.x, p.y, 3.5, 0, 7); g.fill();
        label(p.x, p.y, lonText(c.lon), 'rgba(186,230,253,1)');
    }
    // Words: where the satellite is relative to the crosshair.
    const aim = D.cameraAim(Rm);
    const dAz = D.angleDiff(R.L.azimuth, aim.azimuth), dEl = R.el - aim.elevation;
    const tol = Math.max(1.5, R.bw / 2);
    if (Math.abs(dAz) <= tol && Math.abs(dEl) <= tol) say.textContent = tr('ar_on_target');
    else {
        const parts = [];
        if (Math.abs(dAz) > tol) parts.push(tr(dAz > 0 ? 'ar_right' : 'ar_left', { v: deg(Math.abs(dAz), 0) }));
        if (Math.abs(dEl) > tol) parts.push(tr(dEl > 0 ? 'ar_up' : 'ar_down', { v: deg(Math.abs(dEl), 0) }));
        say.textContent = parts.join(', ');
    }
    const st = lastHeading;
    const bits = [tr('ar_aim', { az: deg(aim.azimuth, 0), el: deg(aim.elevation, 0) })];
    bits.push(AR.calibrated ? tr('ar_cal_on', { v: ltr((AR.cal >= 0 ? '+' : '−') + num(Math.abs(AR.cal), 1) + '°') }) : tr('ar_cal_off'));
    if (st && st.interference) bits.push(tr('compass_iron'));
    info.textContent = bits.join(' · ');
}

// ── drawers ──────────────────────────────────────────────────────────────
function renderObstTable() {
    const t = $('#da-obst-table');
    if (!R || R.L.elevation <= 0) { t.innerHTML = ''; return; }
    const ds = [2, 5, 10, 15, 20, 30, 50, 100];
    t.innerHTML = `<thead><tr><th>${esc(tr('obst_col_d'))}</th><th class="num">${esc(tr('obst_col_h'))}</th></tr></thead><tbody>` +
        ds.map((d) => `<tr><td>${esc(num(d, 0))} ${esc(tr('m'))}</td><td class="num">${esc(num(D.clearance(d, R.el, S.dishH), 1))} ${esc(tr('m'))}</td></tr>`).join('') + '</tbody>';
}
function renderMulti() {
    const out = $('#da-multi-out');
    if (!R) { out.innerHTML = ''; return; }
    const sel = $('#da-multi-sat');
    if (!S.multi || !CAT[S.multi] || S.multi === R.s.id) {
        // The nearest position at least 3° away — what a second LNB is usually for.
        let best = '', bd = 1e9;
        for (const [k, c] of Object.entries(CAT)) {
            const dl = Math.abs(D.angleDiff(c.lon, R.s.lon));
            if (dl >= 3 && dl < bd) { bd = dl; best = k; }
        }
        if (best) sel.value = best;
    }
    const id = sel.value, sec = CAT[id];
    const F = isFinite(S.focal) && S.focal > 5 ? S.focal : Math.round(S.d * 0.65);
    $('#da-focal').placeholder = String(Math.round(S.d * 0.65));
    if (!sec || id === R.s.id) { out.innerHTML = `<p class="tc-msg">${esc(tr('multi_same'))}</p>`; return; }
    const L2 = D.look(S.lat, S.lon, sec.lon);
    if (L2.elevation <= 0) { out.innerHTML = `<p class="tc-msg bad">${esc(tr('below', { el: deg(L2.elevation) }))}</p>`; return; }
    const m = D.multifeed(S.lat, S.lon, R.s.lon, sec.lon, F / 100);
    if (m.sepDeg > 14) { out.innerHTML = `<p class="tc-msg bad">${esc(tr('multi_too_far', { v: deg(m.sepDeg) }))}</p>`; return; }
    const x = Math.abs(m.x), y = Math.abs(m.y);
    const html = [
        `<div class="tc-stats">` +
        stat(tr('multi_sep'), deg(m.sepDeg), tr('multi_sep_sub')) +
        stat(tr('multi_x'), ltr(num(x, 1) + ' ' + tr('cm_short')), tr(m.x < 0 ? 'multi_left' : 'multi_right'), 'lead') +
        stat(tr('multi_y'), ltr(num(y, 1) + ' ' + tr('cm_short')), y < 0.3 ? tr('multi_level') : tr(m.y > 0 ? 'multi_up' : 'multi_down')) +
        `</div>`,
        `<p class="tc-hint da-mt">${esc(tr('multi_how', { f: num(F, 0) }))}</p>`,
    ];
    if (m.sepDeg > 8.5) html.push(`<p class="tc-msg warn">${esc(tr('multi_far'))}</p>`);
    if (sec.pol === 'circular' && R.s.pol !== 'circular') html.push(`<p class="tc-msg">${esc(tr('multi_mixpol'))}</p>`);
    out.innerHTML = html.join('');
    ae('multifeed');
}
function renderOutage() {
    const out = $('#da-outage-out');
    if (!R || R.L.elevation <= 0 || !window.AstroCore) { out.innerHTML = ''; return; }
    const PT = window.__placeTime, tz = PT ? PT.zoneOf(S.lat, S.lon) : null;
    const fmtD = (t) => (PT ? PT.format(new Date(t), tz, LOC, { day: 'numeric', month: 'long' }) : new Date(t).toLocaleDateString(LOC));
    const fmtT = (t) => (PT ? PT.format(new Date(t), tz, LOC, { hour: '2-digit', minute: '2-digit' }) : new Date(t).toLocaleTimeString(LOC));
    const year = new Date().getUTCFullYear();
    const days = [...D.sunOutages(window.AstroCore.sunPos, S.lat, S.lon, R.s.lon, year, R.bw), ...D.sunOutages(window.AstroCore.sunPos, S.lat, S.lon, R.s.lon, year + 1, R.bw)]

    // Consecutive days → one season.
    const seasons = [];
    for (const o of days) {
        const last = seasons[seasons.length - 1];
        if (last && o.peak - last.end.peak < 1.6 * 86400000) { last.end = o; last.maxMin = Math.max(last.maxMin, (o.to - o.from) / 60000); }
        else seasons.push({ start: o, end: o, maxMin: (o.to - o.from) / 60000 });
    }
    const ahead = seasons.filter((x) => x.end.to > Date.now());
    if (!ahead.length) { out.innerHTML = `<p class="tc-msg">${esc(tr('outage_none'))}</p>`; return; }
    out.innerHTML = ahead.slice(0, 2).map((s) => `<p class="tc-msg${s.start.from <= Date.now() ? ' warn' : ''}">${s.start.from <= Date.now() ? esc(tr('outage_now')) + ' ' : ''}${esc(tr('outage_row', {
        from: fmtD(s.start.peak), to: fmtD(s.end.peak), time: fmtT(s.start.peak), min: num(Math.max(1, Math.round(s.maxMin)), 0),
    }))}</p>`).join('') + `<p class="tc-hint da-mt">${esc(tr('outage_dish', { d: S.d }))}</p>`;
    ae('outage');
}

// ── persistence, share ───────────────────────────────────────────────────
function save() {
    if (S.placeSrc === 'default') return;
    try { window.__toolSettings && window.__toolSettings.save(SLUG, 'state', JSON.stringify(S)); } catch (e) { /* storage off */ }
}
function load() {
    try {
        const raw = window.__toolSettings ? window.__toolSettings.load(SLUG, 'state', '') : '';
        if (!raw) return false;
        const o = JSON.parse(raw);
        for (const k of Object.keys(S)) if (o[k] != null && typeof o[k] === typeof S[k]) S[k] = o[k];
        if (S.sat.startsWith('tle:')) loadTle();
        else if (S.sat !== 'custom' && !CAT[S.sat]) S.sat = W.defaultSat;
        return true;
    } catch (e) { return false; }
}
function fromUrl() {
    try {
        const q = new URLSearchParams(location.search);
        const lat = parse(q.get('lat')), lon = parse(q.get('lon'));
        if (isFinite(lat) && isFinite(lon) && Math.abs(lat) <= 90) { S.lat = lat; S.lon = D.wrap180(lon); S.place = q.get('place') || ''; S.placeSrc = 'link'; }
        const s = q.get('sat');
        if (s && CAT[s]) S.sat = s;
        else if (s) {
            const p = D.parseLon(s);
            if (p) { S.sat = 'custom'; S.custom = { value: p.value, side: p.side || 'e' }; }
        }
        return isFinite(lat);
    } catch (e) { return false; }
}
function shareUrl() {
    const q = new URLSearchParams();
    q.set('lat', S.lat.toFixed(5)); q.set('lon', S.lon.toFixed(5));
    if (S.place) q.set('place', S.place);
    const s = sat();
    q.set('sat', S.sat === 'custom' || S.sat.startsWith('tle:') ? (s ? (s.lon >= 0 ? num(s.lon, 2) + 'E' : num(-s.lon, 2) + 'W').replace(',', '.') : '') : S.sat);
    return location.origin + location.pathname + '?' + q.toString();
}
function summary() {
    if (!R) return '';
    const s = R.s, lines = [
        `${tr('copy_head', { sat: s.name, lon: lonText(s.lon) })}`,
        `${tr('copy_place')}: ${S.place ? S.place + ', ' : ''}${latLonText(S.lat, S.lon)}`,
        `${tr('st_az_true')}: ${deg(R.L.azimuth)} (${pointOf(R.L.azimuth)})`,
    ];
    if (R.mag != null) lines.push(`${tr('st_az_mag')}: ${deg(R.mag)}`);
    lines.push(`${tr('st_el')}: ${deg(R.el)}`);
    if (S.kind === 'offset') lines.push(`${tr('st_face')}: ${tiltWords(R.target)}`);
    lines.push(`${tr('st_skew')}: ${s.pol === 'circular' ? tr('skew_none') : skewWords(R.skew)}`);
    lines.push(shareUrl());
    return lines.join('\n');
}
function copyText(text, okMsg) {
    const done = () => toast(okMsg, 'success');
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, () => toast(tr('copy_fail'), 'error'));
    else toast(tr('copy_fail'), 'error');
}

// ── the installer's card (PDF) ────────────────────────────────────────────
/* One A4 page for the person on the roof: the four numbers big, the compass rose, the LNB seen
   from behind, the dish face from the side, when the Sun stands where the satellite is, how high
   the beam is over obstacles, the warnings — and a QR code that reopens this calculation on a
   phone. Drawn as SVG and printed by the vector PDF of the map tools (map-studio/pdf.js): the
   same fonts for Cyrillic and Arabic, crisp at any print size. Colours are plain hex — the PDF
   does not read CSS variables — and nothing is rotated by a transform (the PDF only moves and
   scales), so rotated shapes are computed point by point. */
const plain = (t) => String(t == null ? '' : t).replace(/[\u2066-\u2069]/g, '');
let cardMeasure = null;
function cardTextW(t, size, bold) {
    if (!cardMeasure) cardMeasure = document.createElement('canvas').getContext('2d');
    cardMeasure.font = `${bold ? 700 : 400} ${size}px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
    return cardMeasure.measureText(t).width;
}
function wrapText(t, size, width) {
    const words = plain(t).split(/\s+/), lines = [];
    let line = '';
    for (const w of words) {
        const next = line ? line + ' ' + w : w;
        if (line && cardTextW(next, size) > width) { lines.push(line); line = w; } else line = next;
    }
    if (line) lines.push(line);
    return lines;
}
function loadQr() {
    if (window.qrcode) return Promise.resolve(window.qrcode);
    const urls = (W.assets && W.assets.qr) || ['/vendor/qrcode/qrcode.min.js'];
    const one = (u) => new Promise((res, rej) => { const sc = document.createElement('script'); sc.src = u; sc.async = true; sc.onload = res; sc.onerror = rej; document.head.appendChild(sc); });
    const go = window.__assetChain && window.__assetChain.loadScript ? window.__assetChain.loadScript(urls, 'qrcode') : urls.reduce((p, u) => p.catch(() => one(u)), Promise.reject(new Error('start')));
    return go.then(() => window.qrcode || null).catch(() => null);
}
function qrPath(text, x0, y0, size) {
    const Q = window.qrcode;
    if (!Q) return '';
    let qr;
    try { qr = Q(0, 'M'); qr.addData(text); qr.make(); } catch (e) { return ''; }
    const n = qr.getModuleCount(), m = size / (n + 8), d = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d.push(`M${(x0 + (c + 4) * m).toFixed(2)} ${(y0 + (r + 4) * m).toFixed(2)}h${m.toFixed(2)}v${m.toFixed(2)}h-${m.toFixed(2)}z`);
    return `<rect x="${x0}" y="${y0}" width="${size}" height="${size}" fill="#ffffff"/><path d="${d.join('')}" fill="#000000"/>`;
}
function cardSvg() {
    const r = R, s = r.s, L = r.L;
    const rtl = RTL, VW = 1000, VH = 1414, P = 50;
    const F = "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif";
    const parts = [`<rect width="${VW}" height="${VH}" fill="#ffffff"/>`];
    const T = (x, y, text, size, o = {}) => {
        // Mirrored x only: with direction="rtl" «start» already means the right edge (the PDF honours it);
        // flipping the anchor here as well sent Arabic lines off the right side of the sheet.
        const A = o.anchor || 'start';
        const X = rtl ? VW - x : x;
        parts.push(`<text x="${X.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="${A}"${rtl ? ' direction="rtl"' : ''} font-family="${F}" font-size="${size}" font-weight="${o.bold ? 700 : 400}" fill="${o.color || '#111827'}">${esc(plain(text))}</text>`);
    };
    const box = (x, y, w, h, fill, stroke) => parts.push(`<rect x="${(rtl ? VW - x - w : x).toFixed(1)}" y="${y}" width="${w}" height="${h}" fill="${fill}" stroke="${stroke || 'none'}"/>`);
    let y = 70;
    T(P, y, tr('card_title'), 30, { bold: true }); y += 42;
    T(P, y, `${tr('copy_place')}: ${S.place ? S.place + ', ' : ''}${latLonText(S.lat, S.lon)}`, 17); y += 28;
    // No brackets around a Latin name in an Arabic line: paired brackets across scripts need bidi rule N0.
    T(P, y, tr('card_sat', { sat: s.name, lon: lonText(s.lon) }), 17); y += 28;
    T(P, y, tr('card_dish', { kind: tr(S.kind === 'offset' ? 'card_kind_offset' : 'card_kind_prime', { v: deg(S.offset, 0) }), d: S.d }), 17); y += 26;
    let made = '';
    try { made = new Date().toLocaleDateString(LOC, { day: 'numeric', month: 'long', year: 'numeric' }); } catch (e) { made = window.__localYmd ? window.__localYmd() : ''; }
    T(P, y, tr('card_made', { date: made }), 14, { color: '#64748b' }); y += 30;

    // The four numbers that turn the dish.
    const azMain = r.mag != null ? r.mag : L.azimuth;
    const tiles = [
        [r.mag != null ? tr('st_az_mag') : tr('st_az_true'), deg(azMain), pointOf(L.azimuth)],
        [tr('st_el'), deg(r.el), tr('st_el_sub')],
        [tr('st_face'), deg(Math.abs(r.target)), tiltWords(r.target)],
        [tr('st_skew'), s.pol === 'circular' ? tr('skew_none') : deg(Math.abs(r.skew)), s.pol === 'circular' ? tr('skew_none_sub') : (Math.abs(r.skew) < 0.5 ? tr('skew_zero') : tr(r.skew > 0 ? 'skew_ccw_short' : 'skew_cw_short'))],
    ];
    const tw = (VW - 2 * P - 3 * 14) / 4;
    tiles.forEach((t, i) => {
        const x = P + i * (tw + 14);
        box(x, y, tw, 150, '#f1f5f9', '#cbd5e1');
        const cx = x + 14;
        T(cx, y + 30, t[0], 15, { color: '#334155', bold: true });
        T(cx, y + 82, t[1], 40, { bold: true, color: '#0e7490' });
        wrapText(t[2], 14, tw - 28).slice(0, 2).forEach((ln, k) => T(cx, y + 110 + k * 18, ln, 14, { color: '#475569' }));
    });
    y += 178;
    const second = [`${tr('st_az_true')}: ${deg(L.azimuth)}`];
    if (r.decl != null) second.push(tr('card_decl', { v: (r.decl >= 0 ? '+' : '−') + num(Math.abs(r.decl), 1) + '°' }));
    second.push(`${tr('st_tol')}: ±${num(r.tol, 1)}°`, `${tr('st_range')}: ${num(L.rangeKm, 0)} ${tr('km')}`);
    wrapText(second.join(' · '), 15, VW - 2 * P).forEach((ln) => { T(P, y, ln, 15, { color: '#334155' }); y += 22; });
    y += 14;

    // Drawings: compass rose, the LNB from behind, the dish face from the side.
    const top = y, cy = top + 150;
    const at = (cx0, cy0, rr, a) => [cx0 + rr * Math.sin(a * Math.PI / 180), cy0 - rr * Math.cos(a * Math.PI / 180)];
    {
        const cx = rtl ? VW - P - 150 : P + 150;
        parts.push(`<circle cx="${cx}" cy="${cy}" r="128" fill="#f8fafc" stroke="#94a3b8" stroke-width="2"/>`);
        for (let a = 0; a < 360; a += 30) { const [x1, y1] = at(cx, cy, a % 90 ? 118 : 108, a), [x2, y2] = at(cx, cy, 128, a); parts.push(`<path d="M${x1.toFixed(1)} ${y1.toFixed(1)}L${x2.toFixed(1)} ${y2.toFixed(1)}" fill="none" stroke="#64748b" stroke-width="2"/>`); }
        const [ax, ay] = at(cx, cy, 120, azMain), [lx, ly] = at(cx, cy, 104, azMain - 7), [rx, ry] = at(cx, cy, 104, azMain + 7);
        parts.push(`<path d="M${cx} ${cy}L${ax.toFixed(1)} ${ay.toFixed(1)}" fill="none" stroke="#0e7490" stroke-width="6"/>`);
        parts.push(`<path d="M${ax.toFixed(1)} ${ay.toFixed(1)}L${lx.toFixed(1)} ${ly.toFixed(1)}L${rx.toFixed(1)} ${ry.toFixed(1)}Z" fill="#0e7490"/>`);
        parts.push(`<circle cx="${cx}" cy="${cy}" r="6" fill="#0e7490"/>`);
        // The cardinal letters over the arrow, with a halo: a bearing near south hid «S».
        [[0, 0], [90, 4], [180, 8], [270, 12]].forEach(([a, k]) => { const [x, yy] = at(cx, cy, 92, a); parts.push(`<text x="${x.toFixed(1)}" y="${(yy + 7).toFixed(1)}" text-anchor="middle" font-family="${F}" font-size="20" font-weight="700" fill="#334155" stroke="#f8fafc" stroke-width="5" paint-order="stroke">${esc(POINTS[k])}</text>`); });
        parts.push(`<text x="${cx}" y="${cy + 158}" text-anchor="middle" font-family="${F}" font-size="15" fill="#334155">${esc(plain(tr(r.mag != null ? 'card_rose_mag' : 'card_rose_true')))}</text>`);
    }
    {
        // The LNB seen from behind the dish: body and connector turned by the skew, connector «down» = 0.
        const cx = VW / 2, R0 = 62;
        const a = s.pol === 'circular' ? 0 : -r.skew;
        const rot = (px, py) => { const t = a * Math.PI / 180; const dx = px - cx, dy = py - cy; return [cx + dx * Math.cos(t) - dy * Math.sin(t), cy + dx * Math.sin(t) + dy * Math.cos(t)]; };
        const poly = (pts, fill) => parts.push(`<path d="M${pts.map((q) => rot(q[0], q[1]).map((v) => v.toFixed(1)).join(' ')).join('L')}Z" fill="${fill}"/>`);
        parts.push(`<circle cx="${cx}" cy="${cy}" r="112" fill="none" stroke="#cbd5e1" stroke-width="2" stroke-dasharray="4 6"/>`);
        parts.push(`<path d="M${cx} ${cy}L${cx} ${cy + 116}" fill="none" stroke="#94a3b8" stroke-width="2" stroke-dasharray="5 5"/>`);
        parts.push(`<circle cx="${cx}" cy="${cy}" r="${R0}" fill="#f1f5f9" stroke="#334155" stroke-width="4"/>`);
        parts.push(`<circle cx="${cx}" cy="${cy}" r="${R0 - 22}" fill="none" stroke="#94a3b8" stroke-width="2"/>`);
        poly([[cx - 11, cy + R0 - 4], [cx + 11, cy + R0 - 4], [cx + 11, cy + R0 + 24], [cx - 11, cy + R0 + 24]], '#334155');
        poly([[cx - 6, cy + R0 + 22], [cx + 6, cy + R0 + 22], [cx + 6, cy + R0 + 32], [cx - 6, cy + R0 + 32]], '#64748b');
        if (s.pol !== 'circular' && Math.abs(r.skew) >= 0.5) {
            const cw = a > 0, r1 = 98, from = cw ? -35 : 35, to = cw ? 35 : -35;
            const p0 = at(cx, cy, r1, from), p1 = at(cx, cy, r1, to);
            parts.push(`<path d="M${p0[0].toFixed(1)} ${p0[1].toFixed(1)}A${r1} ${r1} 0 0 ${cw ? 1 : 0} ${p1[0].toFixed(1)} ${p1[1].toFixed(1)}" fill="none" stroke="#0e7490" stroke-width="6"/>`);
            const h1 = at(cx, cy, r1 - 11, to + (cw ? -10 : 10)), h2 = at(cx, cy, r1 + 11, to + (cw ? -10 : 10));
            parts.push(`<path d="M${p1[0].toFixed(1)} ${p1[1].toFixed(1)}L${h1[0].toFixed(1)} ${h1[1].toFixed(1)}L${h2[0].toFixed(1)} ${h2[1].toFixed(1)}Z" fill="#0e7490"/>`);
        }
        parts.push(`<text x="${cx}" y="${cy + 5}" text-anchor="middle" font-family="${F}" font-size="15" font-weight="700" fill="#334155">LNB</text>`);
        parts.push(`<text x="${cx}" y="${cy + 158}" text-anchor="middle" font-family="${F}" font-size="15" fill="#334155">${esc(plain(tr('card_back_view')))}</text>`);
    }
    {
        // The dish face from the side: 0° = vertical, a positive tilt leans back.
        const cx = rtl ? P + 150 : VW - P - 150, gy = cy + 100, Lf = 170;
        // The face's lean from vertical: offset dish — elevation minus offset; prime focus — the elevation itself.
        const tilt = r.target;
        const dir = rtl ? -1 : 1;
        const tp = [cx + dir * Math.sin(tilt * Math.PI / 180) * Lf, gy - Math.cos(tilt * Math.PI / 180) * Lf];
        parts.push(`<path d="M${cx - 130} ${gy}L${cx + 130} ${gy}" fill="none" stroke="#94a3b8" stroke-width="2"/>`);
        parts.push(`<path d="M${cx} ${gy}L${cx} ${gy - Lf}" fill="none" stroke="#94a3b8" stroke-width="2" stroke-dasharray="6 6"/>`);
        parts.push(`<path d="M${cx} ${gy}L${tp[0].toFixed(1)} ${tp[1].toFixed(1)}" fill="none" stroke="#0e7490" stroke-width="8"/>`);
        parts.push(`<text x="${cx}" y="${cy + 158}" text-anchor="middle" font-family="${F}" font-size="15" fill="#334155">${esc(plain(tr('card_side_view', { v: tiltWords(r.target) })))}</text>`);
    }
    y = top + 340;

    // When the Sun stands on the satellite's side: a compass-free check on the roof.
    if (window.AstroCore) {
        const PT = window.__placeTime, tz = PT ? PT.zoneOf(S.lat, S.lon) : null;
        const fmt = (t) => (PT ? PT.format(new Date(t), tz, LOC, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : new Date(t).toLocaleString(LOC));
        const rows = [];
        for (let k = 0; k < 3 && rows.length < 2; k++) for (const h of D.sunAlignTimes(window.AstroCore.sunPos, S.lat, S.lon, L.azimuth, zoneDayStart(tz, k))) if (h.t > Date.now() && rows.length < 2) rows.push(h);
        T(P, y, tr('card_sun_title'), 18, { bold: true }); y += 26;
        if (rows.length) rows.forEach((h) => { T(P, y, `${fmt(h.t)} — ${tr('sun_row_sun', { el: deg(h.sunElevation, 0) })}`, 15, { color: '#334155' }); y += 22; });
        else { T(P, y, tr('sun_none'), 15, { color: '#334155' }); y += 22; }
        y += 14;
    }
    // How high the beam is over the ground ahead.
    T(P, y, tr('card_obst_title'), 18, { bold: true }); y += 26;
    // An en dash, not an arrow: «→» is outside the text face and dragged the whole line into a fallback font.
    const obst = [5, 10, 20, 50, 100].map((d) => `${num(d, 0)} ${tr('m')} – ${num(D.clearance(d, r.el, S.dishH), 1)} ${tr('m')}`).join(' · ');
    wrapText(obst, 15, VW - 2 * P).forEach((ln) => { T(P, y, ln, 15, { color: '#334155' }); y += 22; });
    y += 14;
    const tpLine = tpText(s);
    if (tpLine) {
        T(P, y, tr('card_tp'), 18, { bold: true }); y += 26;
        wrapText(tpLine, 15, VW - 2 * P).forEach((ln) => { T(P, y, ln, 15, { color: '#334155' }); y += 22; });
        y += 14;
    }
    // The warnings the page shows (not the «this is an example» one, not the transponder: it has its block).
    // The page says «the skew shown below»; on the card the skew is in the tiles above.
    const notes = $$('#da-msgs .tc-msg').filter((m) => !/^\s*$/.test(m.textContent) && !m.textContent.includes(tr('example', { place: S.place }).slice(0, 12)) && m.textContent.trim() !== tpLine)
        .map((m) => (m.textContent.trim() === tr('lnb_lin') ? tr('card_lnb_lin') : m.textContent.trim()));
    if (notes.length) {
        T(P, y, tr('card_notes'), 18, { bold: true }); y += 26;
        for (const n of notes.slice(0, 5)) for (const ln of wrapText('• ' + n, 14, VW - 2 * P - 230)) { if (y > VH - 80) break; T(P, y, ln, 14, { color: '#334155' }); y += 20; }
    }
    // The QR: this calculation on a phone, on the roof.
    const qs = 200, qx = rtl ? P : VW - P - qs, qy = VH - P - qs - 40;
    parts.push(qrPath(shareUrl(), qx, qy, qs));
    parts.push(`<text x="${qx + qs / 2}" y="${qy + qs + 24}" text-anchor="middle" font-family="${F}" font-size="14" fill="#475569">${esc(plain(tr('card_qr')))}</text>`);
    const el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    el.setAttribute('viewBox', `0 0 ${VW} ${VH}`);
    el.innerHTML = parts.join('');
    return el;
}
async function downloadCard() {
    if (!R || R.L.elevation <= 0) { toast(tr('need_sat'), 'info'); return; }
    const btn = $('#da-card');
    btn.disabled = true;
    try {
        await loadQr();
        const svg = cardSvg();
        svg.style.cssText = 'position:absolute;left:-99999px;top:0;width:1000px;height:auto';
        document.body.appendChild(svg);
        try {
            const mod = await import('../map-studio/pdf.js');
            const blob = await mod.mapPdf(svg, { title: tr('card_title'), paper: W.paper || 'a4', assets: W.assets || {}, top: true });
            const base = (S.place ? S.place + ' ' : '') + R.s.name;
            const name = window.__outputName ? window.__outputName.sanitize(base) : base.replace(/[\\/:*?"<>|]+/g, '_');
            await window.__downloadFile(blob, (name || 'antenna') + '.pdf');
        } finally { svg.remove(); }
        ae('card', { sat: S.sat.startsWith('tle:') ? 'tle' : S.sat, src: S.placeSrc });
    } catch (e) {
        try { window.__handleError ? window.__handleError(e, 'dish_card', { say: tr('card_fail') }) : toast(tr('card_fail'), 'error'); } catch (_) { toast(tr('card_fail'), 'error'); }
    } finally { btn.disabled = false; }
}

// ── boot ─────────────────────────────────────────────────────────────────
function syncControls() {
    $$('.da-kind .tc-seg-b').forEach((b) => { const on = b.dataset.kind === S.kind; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $('#da-offset-wrap').hidden = S.kind !== 'offset';
    $('#da-rim').hidden = S.kind !== 'offset';
    $('#da-offset').value = num(S.offset, S.offset % 1 ? 1 : 0);
    $$('#da-size .tc-chip').forEach((b) => { const on = +b.dataset.d === S.d; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $('#da-dish-h').value = num(S.dishH, S.dishH % 1 ? 1 : 0);
    if (isFinite(S.focal)) $('#da-focal').value = num(S.focal, 0);
    $$('.da-custom .tc-seg-b').forEach((b) => { const on = b.dataset.side === S.custom.side; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    if (isFinite(S.custom.value)) $('#da-custom-lon').value = num(S.custom.value, S.custom.value % 1 ? 1 : 0);
    if (S.multi && CAT[S.multi]) $('#da-multi-sat').value = S.multi;
}

function boot() {
    const restored = fromUrl() || load();
    if (!restored) S.placeSrc = 'default';

    $$('.da-preset').forEach((b) => b.addEventListener('click', () => setSat(b.dataset.sat, 'preset')));
    $('#da-sat').addEventListener('change', (e) => setSat(e.target.value, 'list'));
    $('#da-custom-lon').addEventListener('input', (e) => {
        const p = D.parseLon(e.target.value);
        if (p) { S.custom.value = p.value; if (p.side) { S.custom.side = p.side; syncControls(); } }
        else S.custom.value = NaN;
        if (S.sat !== 'custom') setSat('custom', 'custom'); else render();
    });
    $$('.da-custom .tc-seg-b').forEach((b) => b.addEventListener('click', () => { S.custom.side = b.dataset.side; syncControls(); render(); }));
    const find = $('#da-find');
    find.addEventListener('focus', loadTle, { once: true });
    find.addEventListener('change', () => {
        const name = find.value.trim();
        if (TLE.byName[name]) setSat('tle:' + name, 'find');
        else {
            const p = D.parseLon(name);
            if (p) { S.custom = { value: p.value, side: p.side || S.custom.side }; syncControls(); setSat('custom', 'find_lon'); }
        }
    });

    $$('.da-kind .tc-seg-b').forEach((b) => b.addEventListener('click', () => { S.kind = b.dataset.kind; userTouched = true; syncControls(); render(); }));
    $('#da-offset').addEventListener('input', (e) => { const x = parse(e.target.value); if (x >= 0 && x <= 45) { S.offset = x; render(); } });
    const rim = () => {
        const w = parse($('#da-rim-w').value), h = parse($('#da-rim-h').value), o = D.offsetFromRim(w, h);
        $('#da-rim-out').textContent = o == null ? (isFinite(w) && isFinite(h) ? tr('rim_bad') : '') : tr('rim_out', { v: deg(o) });
        if (o != null && o > 5 && o < 40) { S.offset = Math.round(o * 10) / 10; syncControls(); render(); ae('rim'); }
    };
    $('#da-rim-w').addEventListener('input', rim);
    $('#da-rim-h').addEventListener('input', rim);
    $$('#da-size .tc-chip').forEach((b) => b.addEventListener('click', () => { S.d = +b.dataset.d; syncControls(); render(); }));
    $('#da-dish-h').addEventListener('input', (e) => { const x = parse(e.target.value); if (x >= 0 && x < 500) { S.dishH = x; renderObstTable(); save(); } });
    $('#da-focal').addEventListener('input', (e) => { S.focal = parse(e.target.value); renderMulti(); save(); });
    $('#da-multi-sat').addEventListener('change', (e) => { S.multi = e.target.value; renderMulti(); save(); });
    $('#da-d-multi').addEventListener('toggle', (e) => { if (e.target.open) renderMulti(); });
    $('#da-d-outage').addEventListener('toggle', (e) => { if (e.target.open) renderOutage(); });

    $$('.da-ways .tc-seg-b').forEach((b) => b.addEventListener('click', () => setWay(b.dataset.way)));
    $('#da-compass-go').addEventListener('click', () => (compassOn ? stopCompass() : startCompass()));
    $('#da-tilt-go').addEventListener('click', () => (tiltOn ? stopTilt() : startTilt()));
    $('#da-ar-go').addEventListener('click', () => (AR.on ? stopAr() : startAr()));
    $('#da-ar-sun').addEventListener('click', calibrateBySun);
    document.addEventListener('visibilitychange', () => { if (document.hidden) { stopCompass(); stopTilt(); stopAr(); } });

    $('#da-copy').addEventListener('click', () => { copyText(summary(), tr('copied')); ae('copy'); });
    $('#da-card').addEventListener('click', downloadCard);
    $('#da-share').addEventListener('click', () => {
        const url = shareUrl();
        ae('share');
        if (navigator.share) navigator.share({ title: document.title, text: summary(), url }).catch(() => { /* closed */ });
        else copyText(url, tr('link_copied'));
    });
    $('#da-obst-btn').addEventListener('click', (e) => {
        obstMode = !obstMode;
        e.currentTarget.setAttribute('aria-pressed', obstMode ? 'true' : 'false');
        e.currentTarget.classList.toggle('on', obstMode);
        $('#da-map-hint').textContent = tr(obstMode ? 'map_hint_obst' : 'map_hint_js');
    });
    $('#da-here-btn').addEventListener('click', () => {
        if (!window.__geo && !navigator.geolocation) { toast(tr('geo_fail'), 'error'); return; }
        const geo = navigator.geolocation;
        geo.getCurrentPosition((p) => setPlace(p.coords.latitude, p.coords.longitude, null, 'geo'), (err) => { toast(tr('geo_fail'), 'error'); diag('geo_fail', { code: err && err.code }); }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 });
    });
    $('#da-live').addEventListener('click', () => { const p = $('#da-answer-panel'); if (p) p.scrollIntoView({ block: 'nearest' }); });
    $$('.da-city').forEach((b) => b.addEventListener('click', () => {
        setPlace(+b.dataset.lat, +b.dataset.lon, b.textContent.trim(), 'table');
        const p = $('#da-answer-panel'); if (p) p.scrollIntoView({ block: 'nearest' });
    }));

    if (window.LocationInput) {
        locWidget = window.LocationInput.mount($('#da-loc'), {
            tool: SLUG,
            initial: S.placeSrc === 'default' ? {} : { lat: S.lat, lng: S.lon, cityName: S.place || null },
            t: W.loc || {},
            onChange: (loc) => setPlace(loc.lat, loc.lng, loc.cityName, loc.source || 'city'),
        });
    }

    syncControls();
    showPlace();
    if (S.sat === 'custom' || !CAT[S.sat]) $('#da-custom').hidden = S.sat !== 'custom';
    setSat(S.sat, 'boot');
    userTouched = S.placeSrc !== 'default';
    setWay(S.way || 'skew');
    $('#da-map-hint').textContent = tr('map_hint_js');

    const mapEl = $('#da-map');
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) { io.disconnect(); initMap(); } }, { rootMargin: '300px' });
        io.observe(mapEl);
    } else initMap();
    // Live orbits are a refinement, not the answer: fetched when the page is idle.
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 2500));
    idle(() => { if (TLE.state === 'idle') loadTle(); });
    stage('ready', { src: S.placeSrc, ms: Math.round(performance.now()) });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
